import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  OnDestroyMixin,
  untilComponentDestroyed,
} from '@w11k/ngx-componentdestroyed';
import {SnackbarService} from '../../../../services/snackbar.service';
import {RoutingService} from '../../../../services/routing.service';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {map, shareReplay, switchMap, take} from 'rxjs/operators';
import {combineLatest, of, Subject} from 'rxjs';
import {Location} from '@angular/common';
import {Router} from '@angular/router';
import {Title} from '@angular/platform-browser';

import {TerraUtils} from '../../../TerraUtils';
import {UserService} from 'src/app/services/shared/user.service';
import AccountType from 'src/app/shared/enums/AccountType.enum';
import UnitDepositAccountStatus from 'src/app/shared/enums/UnitDepositAccountStatus.enum';
import {UnitBankAccountService} from './unit-bank-account.service';
import {UnitApplicationParentType} from 'src/app/shared/enums/unit-bank-account/UnitApplicationParentType.enum';
import {UnitCardService} from 'src/app/services/shared/unit-card.service';
import {UnitCardsSectionComponent} from '../unit-card/unit-cards-section/unit-cards-section.component';
import UnitAccountProvider from '../../../enums/UnitAccountProvider.enum';
import {BankAccountProvider} from 'src/app/dashboard/bankAccounts/enums/bankAccountProvider.enum';
import {UnitAddress} from 'src/app/shared/models/unit-bank-account/UnitAddress.model';
import {BankAccountDto} from 'src/app/shared/models/cre-banking/plaid/bankAccount.model';
import HoldingDiscriminator from 'src/app/shared/enums/HoldingDiscriminator.enum';

@Component({
  selector: 'terra-unit-bank-account',
  templateUrl: './unit-bank-account.component.html',
  styleUrls: ['./unit-bank-account.component.scss'],
  providers: [UnitBankAccountService, UnitCardService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitBankAccountComponent extends OnDestroyMixin implements OnInit {
  @ViewChild('unitCardsSectionComponentMobile')
  cardSection: UnitCardsSectionComponent;

  // consts:
  UnitDepositAccountStatus = UnitDepositAccountStatus;
  UnitAccountType = AccountType;
  UnitAccountProvider = UnitAccountProvider;
  BankAccountProvider = BankAccountProvider;

  unitBankAccount$ = this.unitBankAccountService.bankAccount$.pipe(shareReplay(1));
  bankAccount$ = this.unitBankAccount$.pipe(map(uba => ({
    ...uba,
    bankName: UnitAccountProvider.toString(uba.provider),
    accountHolderName: uba.name,
    accountHolderAddress: this.fullAddress(uba.unitApplication?.address),
    achRoutingNumber: uba.routingNumber,
    wireRoutingNumber: uba.provider === UnitAccountProvider.Thread ? uba.routingNumber : null,
    entityType: uba.unitApplication?.holding ? (uba.unitApplication.holding.discriminator ? HoldingDiscriminator.toString(uba.unitApplication.holding.discriminator) : 'Holding') : 'Business',
    entityName: uba.unitApplication?.holding?.name || uba.unitApplication?.dba,
    entityId: uba.unitApplication?.holdingId?.toString(),
    currentBalance: uba.balance,
    availableBalance: uba.available,
    mask: null
  } as BankAccountDto)));


  isGpBankAccount$ = this.unitBankAccount$.pipe(
    map(
      (bankAccount) =>
        bankAccount.unitApplication?.parentType !== UnitApplicationParentType.LP
    ),
    shareReplay(1)
  );

  isWireSupported$ = this.unitBankAccount$.pipe(
    map((bankAccount) => bankAccount.provider === UnitAccountProvider.Thread)
  );

  accountProvider$ = this.unitBankAccount$.pipe(
    map((bankAccount) => bankAccount.provider)
  );

  accountVisibility = false;

  bankingProvider: UnitAccountProvider;
  bankingProviderString$ = this.accountProvider$.pipe(map(accountProvider => UnitAccountProvider.toProvidedBy(accountProvider)));

  private currentUserId$ = this.userService.accountDetails$.pipe(
    map((accountDetails) => accountDetails.id)
  );

  private isCurrentUserInSignersList$ = combineLatest([
    this.currentUserId$,
    this.unitBankAccount$,
  ]).pipe(
    map(([userId, bankAccount]) =>
      bankAccount.signatureList?.signatures?.some((s) => s.userId === userId)
    ),
    take(1),
    shareReplay(1)
  );

  private currentUserApproved$ = combineLatest([
    this.currentUserId$,
    this.unitBankAccount$,
  ]).pipe(
    map(([userId, bankAccount]) =>
      coerceBooleanProperty(
        bankAccount?.signatureList?.signatures?.find((s) => s.userId === userId)
          ?.signedOnDate
      )
    )
  );

  isUserShouldApprove$ = combineLatest([
    this.isCurrentUserInSignersList$,
    this.currentUserApproved$,
  ]).pipe(
    map(
      ([isCurrentUserInSignersList, currentUserApproved]) =>
        isCurrentUserInSignersList && !currentUserApproved
    )
  );

  organization$ = this.userService.accountDetails$.pipe(
    map((user) => user.organizationDetails)
  );

  @HostBinding("class.gray-background") isPageHasGrayBackground = false;
  @HostBinding("class.lp-host") isLpHost = false;
  backDescription: string = this.router.navigated
    ? "Back"
    : "Bank accounts overview";
  plaidSuccess$ = new EventEmitter<void>();
  closeAccountSuccess$ = new EventEmitter<void>();
  refreshCards$ = new Subject<boolean>();

  constructor(
    private unitBankAccountService: UnitBankAccountService,
    private userService: UserService,
    private location: Location,
    private router: Router,
    private routingService: RoutingService,
    private titleService: Title
  ) {
    super();
  }

  ngOnInit(): void {
    this.unitBankAccount$.pipe(take(1)).subscribe((bankAccount) => {
      this.titleService.setTitle(
        `Bank Account: ${bankAccount.accountNickname} ${bankAccount.unitApplication.parentType ===
          UnitApplicationParentType.LP
          ? TerraUtils.consts.LpPageTitleSuffix
          : TerraUtils.consts.GpPageTitleSuffix
        }`
      );
    });
    this.setBackgroundIfNeeded();
    this.handleEmittersSucceed();
  }

  goBack() {
    window.history.length > 2
      ? this.location.back()
      : this.router.navigateByUrl(this.routingService.creBankAccounts);
  }

  setBackgroundIfNeeded() {
    this.unitBankAccount$
      .pipe(untilComponentDestroyed(this))
      .subscribe((bankAccount) => {
        this.isPageHasGrayBackground =
          bankAccount.status !== UnitDepositAccountStatus.None &&
          bankAccount.unitApplication.parentType !==
          UnitApplicationParentType.LP;
        this.isLpHost =
          bankAccount.unitApplication.parentType ===
          UnitApplicationParentType.LP;
      });
  }

  handleEmittersSucceed() {
    this.plaidSuccess$.pipe(untilComponentDestroyed(this)).subscribe((_) => {
      this.unitBankAccountService.refreshAccountDetails();
    });

    this.closeAccountSuccess$
      .pipe(untilComponentDestroyed(this))
      .subscribe((_) => {
        this.unitBankAccountService.refreshAccountDetails();
        this.refreshCards$.next(true);
      });
  }

  isActiveStatus(status
    :
    UnitDepositAccountStatus
  ):
    boolean {
    return (
      status === UnitDepositAccountStatus.Closed ||
      status === UnitDepositAccountStatus.Open
    );
  }
  fullAddress(address: UnitAddress) {
    return address ? `${address.street}. ${address.city}, ${address.state} ${address.postalCode}` : '';
  }
}
